import type { ApiMarketplaceKey } from "@/marketplaces/marketplaceApiMapping";
import { rewriteLoopbackHostForLanDev } from "@/lib/rewriteLoopbackHostForLanDev";

export type ContentJobEvent =
  | { type: "content.job.started"; payload: { jobId: string; totalImages: number } }
  | { type: "content.job.progress"; payload: { jobId: string; stage: string; progressPercent: number } }
  | { type: "content.job.title.generated"; payload: { jobId: string; title: string } }
  | { type: "content.job.description.generated"; payload: { jobId: string; description: string } }
  | { type: "content.job.image.generated"; payload: { jobId: string; slotIndex: number; url: string; variantId: string } }
  | { type: "content.job.completed"; payload: { jobId: string; partial: { title?: string; description?: string; images: Array<{ slotIndex: number; url: string; variantId: string }> } } }
  | { type: "content.job.failed"; payload: { jobId: string; message: string } };

type CreateContentJobRequest = {
  /**
   * Client-generated stable job id. Persisted on the client BEFORE this request is
   * sent, so a page refresh mid-generation resumes the same job instead of creating
   * a duplicate one (prevents duplicate generation and double GENI spend — BUG-001/002).
   */
  jobId?: string;
  ownerId: string;
  goodId: string;
  versionId?: string;
  marketplace: ApiMarketplaceKey;
  contentType: "photo" | "card";
  productTitle: string;
  productDescription: string;
  generationNotes: string;
  categoryLabel: string;
  activePeriods?: Array<{ start: string; end: string }>;
  characteristics: Array<{ name: string; value: string }>;
  competitors: Array<Record<string, unknown>>;
  sourceImages: Array<{ id: string; url: string }>;
  desiredImageCount?: number;
};

type ContentJobSnapshot = {
  jobId: string;
  status: "queued" | "running" | "completed" | "failed";
  stage: string;
  progressPercent: number;
  partial: {
    title?: string;
    description?: string;
    images: Array<{ slotIndex: number; url: string; variantId: string }>;
  };
};

const CONTENT_API_BASE_URL: string = (() => {
  try {
    const env = (import.meta as { env?: { VITE_CONTENT_API_URL?: string } }).env;
    if (env?.VITE_CONTENT_API_URL?.trim()) {
      return rewriteLoopbackHostForLanDev(env.VITE_CONTENT_API_URL.trim());
    }
  } catch {
    // noop
  }
  return "https://api.geni.cards";
})();

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (payload as { message?: string }).message ||
      `Content API request failed (${response.status})`;
    throw new Error(message);
  }
  return payload as T;
}

export async function createContentJob(body: CreateContentJobRequest): Promise<{ jobId: string }> {
  const response = await fetch(`${CONTENT_API_BASE_URL}/content-jobs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-user-id": body.ownerId,
    },
    body: JSON.stringify(body),
  });
  return parseResponse<{ jobId: string }>(response);
}

export async function getContentJobSnapshot(
  ownerId: string,
  jobId: string,
): Promise<ContentJobSnapshot> {
  const response = await fetch(`${CONTENT_API_BASE_URL}/content-jobs/${jobId}`, {
    method: "GET",
    headers: {
      "x-user-id": ownerId,
    },
  });
  return parseResponse<ContentJobSnapshot>(response);
}

export function subscribeContentJobEvents(
  ownerId: string,
  jobId: string,
  handlers: {
    onEvent: (event: ContentJobEvent) => void;
    onError?: (error: Event) => void;
  },
): () => void {
  const eventSource = new EventSource(
    `${CONTENT_API_BASE_URL}/content-jobs/${jobId}/stream?ownerId=${encodeURIComponent(ownerId)}`,
  );

  const eventNames = [
    "content.job.started",
    "content.job.progress",
    "content.job.title.generated",
    "content.job.description.generated",
    "content.job.image.generated",
    "content.job.completed",
    "content.job.failed",
  ] as const;

  for (const eventName of eventNames) {
    eventSource.addEventListener(eventName, (evt) => {
      try {
        const message = JSON.parse((evt as MessageEvent).data) as {
          eventType: string;
          payload: unknown;
        };
        handlers.onEvent({
          type: eventName,
          payload: message.payload as ContentJobEvent["payload"],
        } as ContentJobEvent);
      } catch (error) {
        console.error("[contentJobs] failed to parse SSE event", error);
      }
    });
  }

  eventSource.onerror = (error) => {
    handlers.onError?.(error);
  };

  return () => {
    eventSource.close();
  };
}
